import { ChangeLevelDirection, RotationDirection, UUID } from '@/types/types';
import {
	ProductModule,
	SerializedProductModule,
} from '../objects/ProductModule';
import { ProjectRenderer } from '../ProjectRenderer';
import {
	LandscapeModule,
	SerializedLandscapeModule,
} from '../objects/LandscapeModule';
import { Box3, Group, Vector3 } from 'three';
import { ModulesGrid } from '../utils/ModulesGrid';
import { Asset } from '@/api/AssetApi';
import { RoomWall } from '../objects/RoomWall';
import { ThreeEvent } from '../types';
import { ColoringManager } from './ColoringManager';
import { ModulesHistoryManager } from './ModulesHistoryManager';

export type SerializedModulesManager = {
	productModules: SerializedProductModule[];
	landscapeModules: SerializedLandscapeModule[];
};

export class ModulesManager {
	productModules: Map<UUID, ProductModule> = new Map();
	landscapeModules: Map<UUID, LandscapeModule> = new Map();
	modulesGrid: ModulesGrid;
	coloringManager: ColoringManager;
	historyManager: ModulesHistoryManager;

	constructor(private renderer: ProjectRenderer) {
		this.modulesGrid = new ModulesGrid(this.renderer);
		this.coloringManager = new ColoringManager(this.renderer);
		this.historyManager = new ModulesHistoryManager(this.renderer);
	}

	getLandscapeModulesForWall(wall: RoomWall) {
		return Array.from(this.landscapeModules.values()).filter(
			(landMod) => landMod.hostWall === wall
		);
	}

	getModule(id: UUID) {
		const productModule = this.productModules.get(id),
			landscapeModule = this.landscapeModules.get(id);

		return productModule || landscapeModule;
	}

	async removeLandscapeModule(id: UUID) {
		const landscapeModule = this.landscapeModules.get(id);
		if (!landscapeModule) return;

		const before = this.historyManager.capture();
		landscapeModule.dispose();
		this.landscapeModules.delete(id);
		this.historyManager.commit(before);
	}

	async addLandscapeModule(
		id: UUID,
		adjustableToWalls: boolean,
		asset?: Asset,
		config?: SerializedLandscapeModule,
		options?: { skipHistory?: boolean }
	) {
		if (!asset) throw new Error('LANDSCAPE_MODULE_MISSING_ASSET');

		const before = options?.skipHistory
			? null
			: this.historyManager.capture();

		const landscapeModule = new LandscapeModule(
			this.renderer,
			id,
			asset,
			adjustableToWalls,
			config
		);

		this.landscapeModules.set(landscapeModule.id, landscapeModule);
		await landscapeModule.ready;

		if (before) this.historyManager.commit(before);

		return landscapeModule;
	}

	async addProductModule(
		id: UUID,
		asset?: Asset,
		config?: SerializedProductModule,
		options?: { skipHistory?: boolean }
	) {
		if (!asset) throw new Error('PRODUCT_MODULE_MISSING_ASSET');

		const before = options?.skipHistory
			? null
			: this.historyManager.capture();

		const productModule = new ProductModule(this.renderer, id, asset, config);

		this.productModules.set(productModule.id, productModule);
		await productModule.ready;

		if (before) this.historyManager.commit(before);

		return productModule;
	}

	async removeProductModule(id: UUID) {
		const productModule = this.productModules.get(id);
		if (!productModule) return;

		const before = this.historyManager.capture();
		productModule.dispose();
		this.productModules.delete(id);
		this.historyManager.commit(before);
	}

	setAvailability() {
		this.setModulesLevelVisibility();
	}

	handleLandscapeModuleDragging(
		landscapeModule: LandscapeModule,
		newPos: Vector3
	) {
		landscapeModule.position = newPos.clone();

		if (!landscapeModule.adjustableToWalls) return;

		const intersectedWalls =
			this.renderer.roomManager.getIntersectedWalls(landscapeModule);

		if (!intersectedWalls.length) {
			landscapeModule.hostWall = null;
			return;
		}

		const hostWall =
			landscapeModule.hostWall &&
			intersectedWalls.includes(landscapeModule.hostWall)
				? landscapeModule.hostWall
				: intersectedWalls[0];

		landscapeModule.snapToWall(hostWall);
	}

	handleProductModuleGroupDragging(
		draggedModules: ProductModule[],
		delta: Vector3
	) {
		const group = new Group();
		for (const module of draggedModules) {
			group.add(module.group);
		}

		group.position.set(...delta.toArray());
	}

	handleProductModuleDragging(
		draggedModule: ProductModule,
		targetPosition: Vector3
	) {
		const idealGridPosition = ModulesGrid.snapModulePositionToGrid(
			targetPosition,
			draggedModule.getBoundingBoxInGridUnits()
		);

		if (!this.modulesGrid.isPositionValid(draggedModule, idealGridPosition))
			return;

		draggedModule.level = this.renderer.currentLevel;
		draggedModule.position = idealGridPosition;
	}

	rotateModule(id: UUID, direction: RotationDirection) {
		const mod = this.getModule(id);
		if (!mod) return;

		if (!(mod instanceof ProductModule)) {
			const before = this.historyManager.capture();
			mod.rotate(direction);
			this.historyManager.commit(before);
			return;
		}

		const before = this.historyManager.capture();
		mod.save();
		this.modulesGrid.unregisterModule(mod);

		mod.rotate(direction);

		if (this.modulesGrid.isPositionValid(mod, mod.position)) {
			this.modulesGrid.registerModule(mod);
			this.historyManager.commit(before);
			return;
		}

		this.renderer.emitter.emit(ThreeEvent.DETECTED_COLLISION, mod.id);

		mod.restore();
		this.modulesGrid.registerModule(mod);
	}

	changeLevel(id: UUID, direction: ChangeLevelDirection) {
		const mod = this.getModule(id);
		if (!mod) return;

		if (!(mod instanceof ProductModule)) return;

		const before = this.historyManager.capture();
		mod.save();
		this.modulesGrid.unregisterModule(mod);

		mod.changeLevel(direction);

		if (this.modulesGrid.isPositionValid(mod, mod.position)) {
			this.modulesGrid.registerModule(mod);
			this.historyManager.commit(before);
			return;
		}

		mod.restore();
		this.modulesGrid.registerModule(mod);
	}

	calculateModulesTotalSurfaceArea() {
		const totalBox = new Box3();

		this.productModules.forEach((prodMod) => {
			totalBox.union(prodMod.box);
		});

		const size = totalBox.getSize(new Vector3());

		return {
			width: Math.round(size.x),
			height: Math.round(size.z),
		};
	}

	setModulesLevelVisibility() {
		this.productModules.forEach((prodMod) => {
			prodMod.setLevelVisibility();
		});
	}

	unHoverAll() {
		this.productModules.forEach((productModule) => {
			productModule.isMouseOver = false;
		});
		this.landscapeModules.forEach((landscapeModule) => {
			landscapeModule.isMouseOver = false;
		});
	}

	clearProductModules() {
		this.productModules.forEach((productModule) => {
			productModule.dispose();
		});
		this.productModules.clear();
	}

	clearLandscapeModules() {
		this.landscapeModules.forEach((landscapeModule) => {
			landscapeModule.dispose();
		});
		this.landscapeModules.clear();
	}

	clearAllModules() {
		this.clearProductModules();
		this.clearLandscapeModules();
		this.modulesGrid.clear();
	}

	export(): SerializedModulesManager {
		return {
			productModules: Array.from(
				this.productModules
					.values()
					.map((productModule) => productModule.export())
			),
			landscapeModules: Array.from(
				this.landscapeModules
					.values()
					.map((landscapeModule) => landscapeModule.export())
			),
		};
	}

	async loadFromData(
		data: SerializedModulesManager,
		options?: { skipHistory?: boolean }
	) {
		const skipHistory = options?.skipHistory ?? true;
		const loadPromises: Promise<unknown>[] = [];

		for (const config of data.productModules) {
			loadPromises.push(
				this.addProductModule(config.id, config.asset, config, {
					skipHistory,
				})
			);
		}
		for (const config of data.landscapeModules) {
			loadPromises.push(
				this.addLandscapeModule(
					config.id,
					config.adjustableToWalls,
					config.asset,
					config,
					{ skipHistory }
				)
			);
		}

		await Promise.all(loadPromises);
		this.setModulesLevelVisibility();
	}

	async restoreFromSnapshot(snapshot: SerializedModulesManager) {
		this.renderer.mouse.cancelActiveInteractions();
		this.clearAllModules();

		await this.loadFromData(structuredClone(snapshot), {
			skipHistory: true,
		});

		this.renderer.emitter.emit(ThreeEvent.MODULES_RESTORED, this.export());
	}
}
